import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginDataPattern } from '@/components/auth';
import { WaveLoading } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { AuthState } from '@/contexts';
import { allowedPaths } from '@/utils/sanitiser';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const { login, state, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as LocationState;
  const from = locationState?.from?.pathname || '/dashboard';

  const isLoading = state === AuthState.Initializing;
  const isAuthenticated = state === AuthState.Authenticated;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const allowedIndex = allowedPaths.indexOf(from);
      if (allowedIndex > -1) {
        navigate(allowedPaths[allowedIndex], { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from]);

  const handleSSOLogin = () => {
    login();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Data Pattern */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <LoginDataPattern
          title="Advanced Analytics"
          subtitle="Empowering wind energy stakeholders to make smarter, faster, and more profitable decisions across the asset lifecycle."
          colorScheme="bright-on-dark"
          height="screen"
          interactive={true}
          className="w-full"
        />
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          {/* Login Form Header */}
          <div className="text-center">
            <h2 className="onyx-h2 text-gray-900">Sign in to your account</h2>
            <p className="mt-2 onyx-body text-gray-600">Access your analytics workspace</p>
          </div>

          {/* Login Form */}
          <div className="mt-8 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-400 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-red-700 onyx-body text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* SSO Login Section */}
            <div className="space-y-4">
              {/* SSO Button */}
              <button
                onClick={handleSSOLogin}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent onyx-button font-fk-medium rounded-lg text-white bg-onyx-bright-blue hover:bg-onyx-dark-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-onyx-bright-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <WaveLoading size="sm" variant="dots" color="bright-blue" />
                    <span className="ml-2">Initializing...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    Sign in with SSO
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-6 border-t border-gray-200">
            <p className="onyx-caption text-gray-500">© 2025 ONYX Insight. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <WaveLoading
          fullScreen={true}
          size="xl"
          variant="wave"
          color="bright-blue"
          text="Initializing authentication..."
        />
      )}
    </div>
  );
};

export default Login;
